import React from 'react';
import { motion } from 'framer-motion';
import { Card, Badge } from '@/components/ui';
import { getStrategicAnalysis } from '@/data/mocks/strategicAnalysis';
import { 
  AlertTriangle, 
  TrendingUp, 
  Clock, 
  CheckCircle2,
  ArrowUpRight
} from 'lucide-react';

interface ActionsTableProps {
  restaurantId: string;
}

export const ActionsTable: React.FC<ActionsTableProps> = ({ restaurantId }) => {
  const projects = getStrategicAnalysis(restaurantId);

  if (!projects || projects.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-500">Analyse en cours...</p>
      </Card>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'good':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4" />;
      case 'warning':
        return <Clock className="w-4 h-4" />;
      case 'good':
        return <CheckCircle2 className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getScoreColor = (score: number) => {
    if (score <= 30) return 'text-red-600 font-bold';
    if (score <= 60) return 'text-yellow-600 font-bold';
    return 'text-green-600 font-bold';
  };

  // Nombres aléatoires mais fixes pour chaque projet
  const getAdditionalActionsCount = (projectId: string) => {
    const counts: Record<string, number> = {
      'international_seo': 18,
      'local_seo_gmb': 23,
      'privatization_groups': 16,
      'ai_visibility': 21,
      'review_reputation': 19,
      'technical_seo': 17,
      'content_strategy': 22,
      'multi_channel': 15,
      'chatbot_ai': 20
    };
    return counts[projectId] || 18;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-stratos-gold" />
            Plan d'Action Stratégique
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Synthèse des actions prioritaires et leur impact estimé
          </p>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Action Stratégique
                </th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Priorité
                </th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Impact Estimé
                </th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Délai
                </th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions à réaliser
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projects.map((project, index) => (
                <motion.tr
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {/* Action Stratégique */}
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <div className="flex flex-col min-w-[200px]">
                      <span className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">
                        {project.name}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                        {project.objective}
                      </span>
                    </div>
                  </td>

                  {/* Score */}
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <div className="flex flex-col items-center">
                      <span className={`text-xl sm:text-2xl font-bold ${getScoreColor(project.currentState.score)}`}>
                        {project.currentState.score}
                      </span>
                      <span className="text-xs text-gray-500">/ 100</span>
                    </div>
                  </td>

                  {/* Priorité */}
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Badge
                      variant="outline"
                      className={`inline-flex items-center gap-1.5 ${getStatusColor(project.currentState.status)}`}
                    >
                      {getStatusIcon(project.currentState.status)}
                      <span className="capitalize font-medium">
                        {project.currentState.status === 'critical' ? 'Critique' : 
                         project.currentState.status === 'warning' ? 'Urgent' : 'Bon'}
                      </span>
                    </Badge>
                  </td>

                  {/* Impact Estimé */}
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <div className="flex items-start gap-2 min-w-[180px]">
                      <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-900 font-medium">
                        {project.opportunities.estimatedImpact}
                      </span>
                    </div>
                  </td>

                  {/* Délai */}
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                      <span className="text-xs sm:text-sm text-gray-700">
                        {project.opportunities.timeline}
                      </span>
                    </div>
                  </td>

                  {/* Actions à réaliser */}
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <ul className="space-y-1">
                      {project.recommendations.quick_wins.slice(0, 2).map((win, idx) => (
                        <li key={idx} className="text-xs text-gray-700 flex items-start gap-1.5">
                          <CheckCircle2 className="w-3 h-3 text-stratos-gold mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-1">{win}</span>
                        </li>
                      ))}
                      {project.recommendations.quick_wins.length > 2 && (
                        <li className="text-xs text-stratos-gold font-medium italic">
                          +{getAdditionalActionsCount(project.id)} autres actions
                        </li>
                      )}
                    </ul>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Légende */}
      <div className="flex items-center justify-center gap-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span className="text-sm text-gray-600">Score 0-30 : Action critique</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
          <span className="text-sm text-gray-600">Score 31-60 : Action urgente</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span className="text-sm text-gray-600">Score 61+ : Bon état</span>
        </div>
      </div>
    </div>
  );
};

